import React, { useState, useEffect, useMemo } from 'react';
import { Switch, Button, Paper, Typography, Box, Stack, styled, useTheme } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import reset from '../../../../assets/icons/Reset.svg';
import PlusIcon from 'packages/novous_web/src/assets/icons/plusBlue.svg';

interface Section {
  id: string;
  name: string;
  active: boolean;
}

export interface AnalyticsSectionsConfig {
  // Safety Analytics Sections
  'analytics-cards'?: boolean;
  'warning-by-category'?: boolean;
  'warning-by-time-bucket'?: boolean;
  'safety-score'?: boolean;
  'hourly-distribution'?: boolean;
  'numerical-analysis'?: boolean;

  // Compliance Analytics Sections - individual cards
  'compliance-analytics-cards'?: boolean;
  'compliance-warning-count'?: boolean;
  'compliance-frequent-violators'?: boolean;
  'compliance-overall-score'?: boolean;
  'compliance-density-map'?: boolean;
  'compliance-lane-departure'?: boolean;
  'compliance-numerical-analysis'?: boolean;
  'compliance-warning-per-km'?: boolean;
  'compliance-compliant-vehicles'?: boolean;
  'compliance-seat-belt-warnings'?: boolean;
  'compliance-weekly-score'?: boolean;
  'compliance-india-heat-map'?: boolean;

  // Productivity Analytics Sections
  'productivity-analytics-cards'?: boolean;
  'productivity-gauge-meter'?: boolean;
  'productivity-utilised-vehicles'?: boolean;
  'productivity-idle-time'?: boolean;
  'productivity-distance-contribution'?: boolean;
  'productivity-day-night-driving'?: boolean;
  'productivity-eco-driving-scatter'?: boolean;
  'productivity-daily-usage'?: boolean;
  'productivity-eco-daily'?: boolean;
  'productivity-downtime-scatter'?: boolean;
  'productivity-driving-breakdown'?: boolean;
  'productivity-daily-breakdown'?: boolean;

  [key: string]: boolean | undefined;
}

interface AnalyticsManageSectionLayoutProps {
  onClose: () => void;
  sectionsConfig: AnalyticsSectionsConfig;
  setSectionsConfig: React.Dispatch<React.SetStateAction<AnalyticsSectionsConfig>>;
  onToggleSection: (sectionId: keyof AnalyticsSectionsConfig) => void;
  currentView: string; // 'Safety', 'Compliance', or 'Productivity'
}

// Default sections for each view type
const getDefaultSections = (view: string): Section[] => {
  if (view.toLowerCase().includes('safety')) {
    return [
      { id: 'analytics-cards', name: 'Analytics Cards', active: true },
      { id: 'warning-by-category', name: 'Warning by Category', active: true },
      { id: 'warning-by-time-bucket', name: 'Warning by Time Bucket', active: true },
      { id: 'safety-score', name: 'Safety Score', active: true },
      { id: 'hourly-distribution', name: 'Hourly Distribution of Driving Events', active: true },
      { id: 'numerical-analysis', name: 'Numerical Analysis of Recent Week', active: true },
    ];
  } else if (view.toLowerCase().includes('compliance')) {
    return [
      { id: 'compliance-analytics-cards', name: 'Analytics Cards', active: true },
      { id: 'compliance-warning-count', name: 'Warning Count', active: true },
      { id: 'compliance-frequent-violators', name: 'Overall Compliance Score', active: true },
      { id: 'compliance-overall-score', name: 'Frequent Violators', active: true },
      { id: 'compliance-density-map', name: 'Compliance Density Map', active: true },
      { id: 'compliance-lane-departure', name: 'Lane Departure with GPS Speed', active: true },
      { id: 'compliance-numerical-analysis', name: 'Numerical Analysis of Recent Week', active: true },
      { id: 'compliance-warning-per-km', name: 'Warning Per 100 Km', active: true },
      { id: 'compliance-compliant-vehicles', name: 'Compliant Vehicles Label', active: true },
      { id: 'compliance-seat-belt-warnings', name: 'Seat Belt Warnings', active: true },
      { id: 'compliance-weekly-score', name: 'Average Compliance Score', active: true },
      { id: 'compliance-india-heat-map', name: 'India Heat Map', active: true },
    ];
  } else if (view.toLowerCase().includes('productivity')) {
    return [
      { id: 'productivity-analytics-cards', name: 'Analytics Cards', active: true },
      {
        id: 'productivity-gauge-meter',
        name: 'Driver Productivity Index',
        active: true,
      },
      {
        id: 'productivity-distance-contribution',
        name: ' Distance Contribution by Vehicle & Date (in KM)',
        active: true,
      },
      { id: 'productivity-idle-time', name: 'Idle Time by Vehicle', active: true },
      {
        id: 'productivity-utilised-vehicles',
        name: 'Most Utilised Vehicles',
        active: true,
      },
      { id: 'productivity-day-night-driving', name: 'Day and Night Driving Hours', active: true },
      { id: 'productivity-driving-breakdown', name: 'Driving Hours Breakdown', active: true },
      { id: 'productivity-daily-usage', name: 'Daily vehicle usage (Day vs Night)', active: true },
      { id: 'productivity-eco-daily', name: 'Daily Eco-Driving Hours per Vehicle', active: true },
      { id: 'productivity-downtime-scatter', name: 'Productivity and Downtime', active: true },
      { id: 'productivity-eco-driving-scatter', name: 'Eco-Driving and Low-Speed Comparison', active: true },
      // { id: 'productivity-daily-breakdown', name: 'Daily time breakdown by vehicle', active: true },
    ];
  }
  return [];
};

const SectionContainer = styled(Box)(({ theme }) => ({
  width: '1115px',
  height: 'auto',
  minHeight: '600px',
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  padding: '32px',
  border: '1px solid #F0EFF8',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '32px',
  opacity: 1,
  [theme.breakpoints.down('md')]: {
    width: '90vw',
    height: 'auto',
    padding: '24px',
  },
}));

const SectionItemWrapper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
  padding: '12px 8px',
  marginBottom: theme.spacing(1),
  backgroundColor: '#F6F6FD',
  borderRadius: '8px',
  opacity: 1,
  transition: theme.transitions.create(['background-color', 'box-shadow']),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AnalyticsManageSectionLayout: React.FC<AnalyticsManageSectionLayoutProps> = ({
  onClose,
  sectionsConfig,
  setSectionsConfig,
  onToggleSection,
  currentView,
}) => {
  const theme = useTheme();
  const [activeSections, setActiveSections] = useState<Section[]>([]);
  const [hiddenSections, setHiddenSections] = useState<Section[]>([]);
  const [isResetAnimating, setIsResetAnimating] = useState(false);
  const [showSectionsLayout, setShowSectionsLayout] = useState(true);

  // Initialize sections based on current view
  useEffect(() => {
    const defaultSections = getDefaultSections(currentView);
    const updatedSections = defaultSections.map((section) => ({
      ...section,
      active: sectionsConfig[section.id] !== undefined ? sectionsConfig[section.id]! : section.active,
    }));

    const updatedActiveSections = updatedSections.filter((section) => section.active);
    const updatedHiddenSections = updatedSections.filter((section) => !section.active);

    setActiveSections(updatedActiveSections);
    setHiddenSections(updatedHiddenSections);
  }, [sectionsConfig, currentView]);

  const handleToggleActive = (section: Section) => {
    setActiveSections((prev) => prev.filter((s) => s.id !== section.id));
    setHiddenSections((prev) => [...prev, { ...section, active: false }]);
  };

  const handleToggleHidden = (section: Section) => {
    setHiddenSections((prev) => prev.filter((s) => s.id !== section.id));
    setActiveSections((prev) => [...prev, { ...section, active: true }]);
  };

  const handleSave = () => {
    // Preserve existing sectionsConfig and only update sections for current view
    const newConfig = { ...sectionsConfig } as AnalyticsSectionsConfig;
    [...activeSections, ...hiddenSections].forEach((section) => {
      newConfig[section.id] = section.active;
    });
    setSectionsConfig(newConfig);
    onClose();
  };

  const handleReset = () => {
    const defaultSections = getDefaultSections(currentView);
    setActiveSections(defaultSections);
    setHiddenSections([]);
    setIsResetAnimating(true);
    setTimeout(() => setIsResetAnimating(false), 500);
  };

  const toggleSectionsLayout = () => {
    setShowSectionsLayout((prev) => !prev);
  };

  const SectionItem: React.FC<{ section: Section; onToggle: () => void; isInactive?: boolean }> = ({
    section,
    onToggle,
    isInactive = false,
  }) => (
    <SectionItemWrapper
      elevation={0}
      sx={
        isInactive
          ? {
              backgroundColor: '#F7F7F7',
            }
          : {}
      }
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
        <DragIndicatorIcon
          sx={{
            width: '24px',
            height: '24px',
            opacity: 1,
            color: isInactive ? '#E0E0E0' : '#D4D1F6',
            position: 'relative',
          }}
        />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: '0%',
            color: isInactive ? '#808080' : '#1B1B1B',
          }}
        >
          {section.name}
        </Typography>
      </Stack>
      <Switch checked={section.active} onChange={onToggle} color="primary" />
    </SectionItemWrapper>
  );

  const renderSectionPair = (section1: Section, section2: Section | undefined, pairIndex: number) => (
    <Stack direction="row" spacing={2} key={section1.id} sx={{ width: '100%' }}>
      <Box sx={{ flex: 1 }}>
        <SectionItem section={section1} onToggle={() => handleToggleActive(section1)} />
      </Box>
      {section2 && (
        <Box sx={{ flex: 1 }}>
          <SectionItem section={section2} onToggle={() => handleToggleActive(section2)} />
        </Box>
      )}
    </Stack>
  );

  return (
    <SectionContainer>
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '36px',
              letterSpacing: '0%',
              color: '#1B1B1B',
            }}
          >
            Customize Your Dashboard
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReset}
            startIcon={
              <img
                src={reset}
                alt="Reset"
                style={{
                  width: 16,
                  height: 16,
                  transition: theme.transitions.create('transform'),
                  transform: isResetAnimating ? 'rotate(180deg)' : 'none',
                }}
              />
            }
            sx={{
              fontWeight: 700,
              fontSize: '12px',
              lineHeight: '18px',
              letterSpacing: '0%',
              color: '#5547DD',
              textTransform: 'none',
              py: 1,
              px: 2,
              borderRadius: '8px',
              borderWidth: '1px',
              border: '1px solid #5547DD',
              '&:hover': {
                borderWidth: '1px',
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Reset
          </Button>
        </Stack>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0%',
            color: '#767676',
          }}
        >
          Choose which sections to display and arrange them the way you like.
        </Typography>
      </Stack>

      {/* Sections & Layout */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ userSelect: 'none' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '27px',
              letterSpacing: '0%',
              color: '#1B1B1B',
            }}
          >
            Sections & Layout
          </Typography>
          <Button
            variant="text"
            startIcon={
              <img
                src={PlusIcon}
                alt="Add Custom Chart"
                style={{
                  width: 24,
                  height: 24,
                  opacity: 1,
                }}
              />
            }
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '0%',
              color: '#5547DD',
              textTransform: 'none',
            }}
          >
            Add Custom Chart
          </Button>
        </Stack>

        <Stack spacing={1} sx={{ mt: 2 }}>
          {activeSections
            .reduce((pairs: Section[][], section, index) => {
              if (index % 2 === 0) {
                pairs.push([section, activeSections[index + 1]].filter(Boolean));
              }
              return pairs;
            }, [])
            .map((pair, pairIndex) => renderSectionPair(pair[0], pair[1], pairIndex))}

          {hiddenSections.length > 0 && (
            <>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                  mt: 2,
                  mb: 1,
                }}
              >
                Hidden Sections
              </Typography>
              {hiddenSections.map((section) => (
                <SectionItem
                  key={section.id}
                  section={section}
                  onToggle={() => handleToggleHidden(section)}
                  isInactive={true}
                />
              ))}
            </>
          )}
        </Stack>
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 'auto', pt: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '8px',
            borderWidth: '1px',
            border: '1px solid #5547DD',
            color: '#5547DD',
            '&:hover': {
              borderWidth: '1px',
              backgroundColor: 'rgba(85, 71, 221, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '8px',
            backgroundColor: '#5547DD',
            '&:hover': {
              backgroundColor: '#4039b3',
            },
          }}
        >
          Save
        </Button>
      </Stack>
    </SectionContainer>
  );
};

export default AnalyticsManageSectionLayout;
